import { createContext, useContext, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    calculateTorisphericalVessel,
    calculateCylindricalShell,
    calculateDishedHead,
    calculateBudgetCost,
    getBudgetDimensions,
} from "../services/budgets/BudgetService";
import Notify from "../components/global/Notify";

const CalculationContext = createContext(null);

export function CalculationProvider({ children }) {
    const { i18n } = useTranslation();
    const [budgetId, setBudgetId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState({
        vessel: null,
        cylindrical: null,
        dishedHead: null,
        dimensions: null,
        budgetCost: null,
    });

    const runStepCalculation = useCallback(async (step) => {
        if (!budgetId || step === 0 || step === 2) return;
        setLoading(true);
        setError(null);
        try {
            if (step === 1) {
                const data = await calculateTorisphericalVessel(budgetId);
                setResults(prev => ({ ...prev, vessel: data }));
            } else if (step === 3) {
                const data = await calculateCylindricalShell(budgetId);
                setResults(prev => ({ ...prev, cylindrical: data }));
            } else if (step === 4) {
                const data = await calculateDishedHead(budgetId);
                setResults(prev => ({ ...prev, dishedHead: data }));
            } else if (step === 5) {
                const [dimensions, budgetCost] = await Promise.all([
                    getBudgetDimensions(budgetId),
                    calculateBudgetCost(budgetId, i18n.language),
                ]);
                setResults(prev => ({ ...prev, dimensions, budgetCost }));
            }
        } catch (err) {
            setError(err);
            Notify.error("Erro ao efetuar os cálculos");
        } finally {
            setLoading(false);
        }
    }, [budgetId, i18n.language]);

    return (
        <CalculationContext.Provider value={{ budgetId, setBudgetId, results, loading, error, runStepCalculation }}>
            {children}
        </CalculationContext.Provider>
    );
}

export function useCalculation() {
    const ctx = useContext(CalculationContext);
    if (!ctx) throw new Error("useCalculation must be used inside CalculationProvider");
    return ctx;
}
